import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { PhotoShootImageData } from 'apis/mips/types';

import { ImageSlot } from './ImageSlot';
import { DroppableImageSlotData } from '../../utils/typeGuards';

type OnUnlinkArgs = { itemId: string; imageId: string };
type SortableImageSlotProps = {
  id: string;
  data: Omit<DroppableImageSlotData, 'imageSlotIndex'> & {
    imageSlotIndex?: number;
  };
  image?: PhotoShootImageData;
  iconSize?: number;
  isLoading?: boolean;
  isReadOnly?: boolean;
  onUnlink?: ({ itemId, imageId }: OnUnlinkArgs) => void;
};

const Root = styled.div`
  width: 100%;
  height: 100%;
  cursor: grab;
`;

export const SortableImageSlot = ({
  id,
  data,
  image,
  iconSize,
  isLoading,
  isReadOnly,
  onUnlink,
}: SortableImageSlotProps) => {
  const droppableData = useMemo(() => ({ ...data, imageSlotIndex: data.imageSlotIndex ?? 0 }), [data]);

  const { attributes, listeners, setNodeRef, transform, isDragging, isOver, transition } = useSortable({
    id,
    data: droppableData,
    disabled: Boolean(isReadOnly) || !image,
  });

  const handleUnlink = useCallback(() => {
    if (!image) {
      return;
    }

    onUnlink?.({ itemId: data.id, imageId: image.id });
  }, [data, image, onUnlink]);

  const style = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <Root ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ImageSlot
        imageUrl={image?.url}
        isReadOnly={isReadOnly}
        isLoading={isLoading}
        isOver={isOver}
        onUnlink={handleUnlink}
        iconSize={iconSize}
      />
    </Root>
  );
};
